import React from 'react';
import { scoreTier } from '../SpineScore';

interface CaseFileScoreModuleProps {
  score: number | null;
  confidence?: number | null;
}

/**
 * Evidence Board direction: dark sidebar module — big number, tier pill,
 * a real confidence bar (not a fabricated percentile/sparkline — we don't
 * have a precomputed distribution to rank against, so we only show what's real).
 */
export const CaseFileScoreCard: React.FC<CaseFileScoreModuleProps> = ({ score, confidence }) => {
  const hasScore = score !== null;
  const tier = hasScore ? scoreTier(score) : null;

  return (
    <div className="border-t border-navy-700 pt-4">
      <div className="font-bold text-[8px] text-navy-300 uppercase tracking-[0.2em] mb-1.5">Spine score</div>
      <div className="flex items-end gap-2 mb-2">
        <span className="font-black text-[54px] text-cream-50 leading-[0.85]">{hasScore ? score : '--'}</span>
        {tier && (
          <span
            className="font-black text-[11px] text-cream-50 px-1.5 py-0.5 uppercase tracking-widest mb-1.5"
            style={{ backgroundColor: tier.color }}
          >
            {tier.label}
          </span>
        )}
      </div>
      {hasScore ? (
        <>
          <div className="h-[7px] bg-navy-800 border border-navy-700">
            <div className="h-full" style={{ width: `${score}%`, backgroundColor: tier?.color }} />
          </div>
          {typeof confidence === 'number' && (
            <div className="font-bold text-[8px] text-navy-300 uppercase tracking-[0.12em] mt-1.5">
              {Math.round(confidence * 100)}% confidence
            </div>
          )}
        </>
      ) : (
        <div className="font-bold text-[9px] text-navy-300 uppercase tracking-[0.1em] mt-1.5 leading-relaxed">
          Not enough public record yet — common for newly elected/appointed officials.
        </div>
      )}
    </div>
  );
};

/**
 * Field Report direction: compact registry-row score — number stacked over
 * "Spine" label, color-coded by tier. Used inline in list/table rows.
 */
export const CaseFileScoreRow: React.FC<CaseFileScoreModuleProps> = ({ score }) => {
  const hasScore = score !== null;
  const tier = hasScore ? scoreTier(score) : null;

  return (
    <div className="text-right flex-none">
      <div className="font-black text-[22px] leading-none" style={{ color: tier?.color ?? '#5A7FA5' }}>
        {hasScore ? score : '--'}
      </div>
      <div className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.14em] mt-0.5">Spine</div>
    </div>
  );
};
